import { EphemeralStore } from 'loro-crdt';
/**
 * Ephemeral peer-presence store backed by Loro's `EphemeralStore`.
 *
 * `setLocal` publishes this peer's state, `apply` ingests a remote frame, and
 * `all` returns the latest states for every known peer. Values are constrained
 * to the JSON-like shape Loro supports. Call {@link dispose} when the room shuts
 * down to unsubscribe and free the store.
 */
export class Presence {
    store;
    peerId;
    broadcast;
    timeoutMs;
    unsub;
    /** @param options - Peer identity, broadcast, and expiry options for presence. */
    constructor(options) {
        this.peerId = options.peerId;
        this.broadcast = options.broadcast;
        this.timeoutMs = options.timeoutMs ?? 30_000;
        this.store = new EphemeralStore(this.timeoutMs);
        this.unsub = this.store.subscribeLocalUpdates((bytes) => this.broadcast(bytes));
    }
    /**
     * Publishes this peer's presence state to connected clients.
     *
     * @param state - Complete presence state for this peer.
     */
    setLocal(state) {
        this.store.set(this.peerId, state);
    }
    /**
     * Ingests a remote presence update.
     *
     * @param frame - Encoded remote presence update.
     */
    apply(frame) {
        this.store.apply(frame);
    }
    /**
     * Ingests a remote presence frame, rebinding it to `connectionKey`
     * regardless of what peer key the payload itself claims — a client can
     * never publish presence under another connection's identity this way.
     *
     * Falls back to trusting the payload's own key when `connectionKey` is
     * unavailable (e.g. a direct/test call with no WebSocket connection to
     * bind to).
     *
     * @param connectionKey - The sending connection's own identity (its
     *   WebSocket connection id), never client-suppliable.
     * @param frame - Untrusted remote presence update.
     * @returns The re-keyed frame to relay to other peers, or `undefined` if
     *   the frame carried anything other than exactly one peer's state.
     */
    applyFor(connectionKey, frame) {
        if (!connectionKey) {
            this.store.apply(frame);
            return frame;
        }
        const scratch = new EphemeralStore(this.timeoutMs);
        try {
            scratch.apply(frame);
            const keys = scratch.keys();
            if (keys.length !== 1 || !keys[0])
                return undefined;
            scratch.set(connectionKey, scratch.get(keys[0]));
            const rekeyed = scratch.encode(connectionKey);
            this.store.apply(rekeyed);
            return rekeyed;
        }
        finally {
            scratch.destroy();
        }
    }
    /**
     * Lists current presence.
     *
     * @returns The latest presence states keyed by peer id.
     */
    all() {
        return this.store.getAllStates();
    }
    /**
     * Encodes every live peer state as one update frame — what a late joiner
     * needs to see who is already in the room without waiting a heartbeat.
     */
    encodeAll() {
        return this.store.encodeAll();
    }
    /** Unsubscribes local-update broadcasting and frees the ephemeral store. */
    dispose() {
        this.unsub?.();
        this.store.destroy();
    }
}
//# sourceMappingURL=presence.js.map